/**
 * FilterPanel — Faceted filter sidebar for product listings
 *
 * Desktop: 280px left sidebar, filters auto-apply on change.
 * Mobile: slide-out drawer, filters apply on "Apply" button click.
 *
 * Supports two facet types from FACET_CONFIG:
 *   - checkbox: unique values with counts, "Show more" after 5 items
 *   - range: dual-thumb slider with min/max number inputs
 *
 * Active filters shown as removable pills at the top.
 */

import { useState, useMemo, useCallback, useRef, useEffect } from 'react';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function resolveField(facet) {
  if (typeof facet.field === 'string') return facet.field;
  if (facet.field?.metric) return facet.field.metric;
  return facet.id;
}

function resolveUnit(facet) {
  if (typeof facet.unit === 'string') return facet.unit;
  if (facet.unit?.metric) return facet.unit.metric;
  return '';
}

function getValueLabel(facet, value) {
  if (facet.valueLabels && facet.valueLabels[value]) {
    return facet.valueLabels[value];
  }
  return String(value);
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function ActiveFilterPills({ facets, activeFilters, onRemove }) {
  const pills = [];

  facets.forEach((facet) => {
    const value = activeFilters[facet.id];
    if (!value) return;

    if (facet.type === 'checkbox' && Array.isArray(value) && value.length > 0) {
      value.forEach((v) => {
        pills.push({
          key: `${facet.id}-${v}`,
          label: `${facet.label}: ${getValueLabel(facet, v)}`,
          onRemove: () => {
            const next = value.filter((x) => x !== v);
            onRemove(facet.id, next.length > 0 ? next : undefined);
          },
        });
      });
    } else if (facet.type === 'range' && (value.min != null || value.max != null)) {
      const unit = resolveUnit(facet);
      const parts = [];
      if (value.min != null) parts.push(`${value.min}`);
      parts.push('–');
      if (value.max != null) parts.push(`${value.max}`);
      if (unit) parts.push(unit);
      pills.push({
        key: facet.id,
        label: `${facet.label}: ${parts.join(' ')}`,
        onRemove: () => onRemove(facet.id, undefined),
      });
    }
  });

  if (pills.length === 0) return null;

  return (
    <div className="flex flex-wrap gap-2 pb-4 border-b border-n2">
      {pills.map((pill) => (
        <span
          key={pill.key}
          className="inline-flex items-center gap-1 px-2.5 py-1 bg-n1 text-xs
                     font-sandvik-text text-carbon-black border border-n2 rounded-none"
        >
          {pill.label}
          <button
            type="button"
            onClick={pill.onRemove}
            aria-label={`Remove filter: ${pill.label}`}
            className="ml-0.5 text-n5 hover:text-carbon-black transition-colors"
          >
            <span className="material-symbols-outlined text-sm">close</span>
          </button>
        </span>
      ))}
    </div>
  );
}

function CheckboxFacet({ facet, products, activeValues, onChange }) {
  const [expanded, setExpanded] = useState(false);
  const [isOpen, setIsOpen] = useState(true);
  const field = resolveField(facet);

  const options = useMemo(() => {
    const counts = {};
    products.forEach((p) => {
      const val = p[field];
      if (val == null || val === '') return;
      const values = Array.isArray(val) ? val : [val];
      values.forEach((v) => {
        const key = String(v);
        counts[key] = (counts[key] || 0) + 1;
      });
    });

    return Object.entries(counts)
      .map(([value, count]) => ({ value, count }))
      .sort((a, b) => b.count - a.count || a.value.localeCompare(b.value));
  }, [products, field]);

  const INITIAL_SHOW = 5;
  const visibleOptions = expanded ? options : options.slice(0, INITIAL_SHOW);
  const hasMore = options.length > INITIAL_SHOW;
  const selected = activeValues || [];

  const handleToggle = (value) => {
    const next = selected.includes(value)
      ? selected.filter((v) => v !== value)
      : [...selected, value];
    onChange(facet.id, next.length > 0 ? next : undefined);
  };

  return (
    <div className="border-b border-n2">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center justify-between w-full py-3 text-left
                   font-sandvik-text text-sm font-medium text-carbon-black
                   hover:text-electric-blue transition-colors"
        aria-expanded={isOpen}
      >
        {facet.label}
        {selected.length > 0 && (
          <span className="mr-auto ml-2 text-xs text-electric-blue font-normal">
            ({selected.length})
          </span>
        )}
        <span className="material-symbols-outlined text-lg text-n5 transition-transform duration-200"
              style={{ transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}>
          expand_more
        </span>
      </button>

      {isOpen && (
        <div className="pb-3 space-y-1.5">
          {visibleOptions.map(({ value, count }) => (
            <label
              key={value}
              className="flex items-center gap-2 cursor-pointer group py-0.5"
            >
              <input
                type="checkbox"
                checked={selected.includes(value)}
                onChange={() => handleToggle(value)}
                className="w-4 h-4 rounded-none border-n3 text-electric-blue
                           focus:ring-electric-blue focus:ring-offset-0 accent-electric-blue"
              />
              <span className="flex-1 text-sm text-n6 font-sandvik-text group-hover:text-carbon-black truncate">
                {getValueLabel(facet, value)}
              </span>
              <span className="text-xs text-n4 font-sandvik-text tabular-nums">
                ({count})
              </span>
            </label>
          ))}

          {hasMore && (
            <button
              type="button"
              onClick={() => setExpanded(!expanded)}
              className="text-xs text-electric-blue hover:underline font-sandvik-text mt-1"
            >
              {expanded ? 'Show less' : `Show all ${options.length}`}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * RangeFacet — dual-thumb slider with min/max inputs
 *
 * Renders a native dual-range slider (two overlaid <input type="range">)
 * with a coloured track between the thumbs, plus min/max number inputs.
 * Matches the live MRT app's orange slider appearance.
 */
function RangeFacet({ facet, products, activeRange, onChange }) {
  const [isOpen, setIsOpen] = useState(true);
  const field = resolveField(facet);
  const unit = resolveUnit(facet);
  const trackRef = useRef(null);

  // Compute min/max from full product set
  const { dataMin, dataMax, step } = useMemo(() => {
    let min = Infinity;
    let max = -Infinity;
    products.forEach((p) => {
      const val = parseFloat(p[field]);
      if (!isNaN(val)) {
        if (val < min) min = val;
        if (val > max) max = val;
      }
    });
    const dMin = min === Infinity ? 0 : Math.floor(min);
    const dMax = max === -Infinity ? 0 : Math.ceil(max);
    // Step: use 1 for integer-ish ranges, 0.1 for small ranges
    const range = dMax - dMin;
    const s = range > 100 ? 1 : range > 10 ? 0.5 : 0.1;
    return { dataMin: dMin, dataMax: dMax, step: s };
  }, [products, field]);

  // Current slider values (fall back to data bounds)
  const sliderMin = activeRange?.min ?? dataMin;
  const sliderMax = activeRange?.max ?? dataMax;

  // Percentage for track highlight
  const range = dataMax - dataMin || 1;
  const leftPct = ((sliderMin - dataMin) / range) * 100;
  const rightPct = ((sliderMax - dataMin) / range) * 100;

  const handleSliderMin = (e) => {
    const val = parseFloat(e.target.value);
    const clamped = Math.min(val, sliderMax);
    const next = { ...(activeRange || {}), min: clamped };
    if (clamped <= dataMin && (next.max == null || next.max >= dataMax)) {
      onChange(facet.id, undefined);
    } else {
      onChange(facet.id, next);
    }
  };

  const handleSliderMax = (e) => {
    const val = parseFloat(e.target.value);
    const clamped = Math.max(val, sliderMin);
    const next = { ...(activeRange || {}), max: clamped };
    if (clamped >= dataMax && (next.min == null || next.min <= dataMin)) {
      onChange(facet.id, undefined);
    } else {
      onChange(facet.id, next);
    }
  };

  const handleInputChange = (key, rawValue) => {
    const numValue = rawValue === '' ? undefined : parseFloat(rawValue);
    const current = activeRange || {};
    const next = { ...current, [key]: numValue };
    if (next.min == null && next.max == null) {
      onChange(facet.id, undefined);
    } else {
      onChange(facet.id, next);
    }
  };

  // Inline styles for the dual-range slider
  const sliderStyles = `
    .range-slider-container {
      position: relative;
      height: 24px;
      margin: 8px 0;
    }
    .range-slider-container input[type="range"] {
      position: absolute;
      width: 100%;
      top: 0;
      height: 24px;
      pointer-events: none;
      -webkit-appearance: none;
      appearance: none;
      background: transparent;
      margin: 0;
      z-index: 2;
    }
    .range-slider-container input[type="range"]::-webkit-slider-thumb {
      -webkit-appearance: none;
      appearance: none;
      width: 18px;
      height: 18px;
      border-radius: 50%;
      background: #F54B00;
      border: 2px solid white;
      box-shadow: 0 1px 3px rgba(0,0,0,0.3);
      cursor: pointer;
      pointer-events: all;
      position: relative;
      z-index: 3;
    }
    .range-slider-container input[type="range"]::-moz-range-thumb {
      width: 18px;
      height: 18px;
      border-radius: 50%;
      background: #F54B00;
      border: 2px solid white;
      box-shadow: 0 1px 3px rgba(0,0,0,0.3);
      cursor: pointer;
      pointer-events: all;
    }
    .range-slider-track {
      position: absolute;
      top: 10px;
      left: 0;
      right: 0;
      height: 4px;
      background: #E5E5E5;
      border-radius: 2px;
      z-index: 1;
    }
    .range-slider-track-active {
      position: absolute;
      top: 10px;
      height: 4px;
      background: #F54B00;
      border-radius: 2px;
      z-index: 1;
    }
  `;

  return (
    <div className="border-b border-n2">
      <style>{sliderStyles}</style>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center justify-between w-full py-3 text-left
                   font-sandvik-text text-sm font-medium text-carbon-black
                   hover:text-electric-blue transition-colors"
        aria-expanded={isOpen}
      >
        {facet.label}
        {unit && <span className="text-xs text-n4 font-normal ml-1">({unit})</span>}
        <span className="material-symbols-outlined text-lg text-n5 transition-transform duration-200"
              style={{ transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}>
          expand_more
        </span>
      </button>

      {isOpen && dataMax > dataMin && (
        <div className="pb-4">
          {/* Dual-thumb slider */}
          <div className="range-slider-container" ref={trackRef}>
            {/* Background track */}
            <div className="range-slider-track" />
            {/* Active (coloured) track segment */}
            <div
              className="range-slider-track-active"
              style={{ left: `${leftPct}%`, width: `${rightPct - leftPct}%` }}
            />
            {/* Min thumb */}
            <input
              type="range"
              min={dataMin}
              max={dataMax}
              step={step}
              value={sliderMin}
              onChange={handleSliderMin}
              aria-label={`${facet.label} minimum`}
            />
            {/* Max thumb */}
            <input
              type="range"
              min={dataMin}
              max={dataMax}
              step={step}
              value={sliderMax}
              onChange={handleSliderMax}
              aria-label={`${facet.label} maximum`}
            />
          </div>

          {/* Min/Max number inputs below */}
          <div className="flex items-center gap-2 mt-2">
            <div className="flex-1">
              <input
                type="number"
                value={activeRange?.min ?? ''}
                onChange={(e) => handleInputChange('min', e.target.value)}
                placeholder={String(dataMin)}
                min={dataMin}
                max={dataMax}
                step="any"
                className="w-full px-2 py-1 border border-n2 rounded-none text-xs
                           font-sandvik-text text-carbon-black placeholder:text-n4
                           focus:outline-none focus:border-electric-blue"
              />
            </div>
            <span className="text-n4 text-xs">&ndash;</span>
            <div className="flex-1">
              <input
                type="number"
                value={activeRange?.max ?? ''}
                onChange={(e) => handleInputChange('max', e.target.value)}
                placeholder={String(dataMax)}
                min={dataMin}
                max={dataMax}
                step="any"
                className="w-full px-2 py-1 border border-n2 rounded-none text-xs
                           font-sandvik-text text-carbon-black placeholder:text-n4
                           focus:outline-none focus:border-electric-blue"
              />
            </div>
            {unit && (
              <span className="text-xs text-n4 font-sandvik-text">{unit}</span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export default function FilterPanel({
  facets = [],
  products = [],
  activeFilters = {},
  onFilterChange,
  onClearAll,
  isMobile = false,
  onClose,
}) {
  const [pendingFilters, setPendingFilters] = useState(null);

  const effectiveFilters = isMobile && pendingFilters != null
    ? pendingFilters
    : activeFilters;

  const hasActiveFilters = Object.values(activeFilters).some((v) => v != null);

  const handleFilterChange = useCallback(
    (facetId, value) => {
      if (isMobile) {
        setPendingFilters((prev) => {
          const base = prev != null ? prev : { ...activeFilters };
          if (value === undefined) {
            const next = { ...base };
            delete next[facetId];
            return next;
          }
          return { ...base, [facetId]: value };
        });
      } else {
        onFilterChange?.(facetId, value);
      }
    },
    [isMobile, activeFilters, onFilterChange]
  );

  const handleApply = () => {
    if (pendingFilters != null) {
      Object.entries(pendingFilters).forEach(([facetId, value]) => {
        onFilterChange?.(facetId, value);
      });
      Object.keys(activeFilters).forEach((facetId) => {
        if (!(facetId in pendingFilters)) {
          onFilterChange?.(facetId, undefined);
        }
      });
      setPendingFilters(null);
    }
    onClose?.();
  };

  const handleClearAll = () => {
    if (isMobile) {
      setPendingFilters({});
    } else {
      onClearAll?.();
    }
  };

  const panelContent = (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between pb-3 border-b border-n2">
        <h2 className="font-sandvik-headline text-base font-normal text-carbon-black">
          Filters
        </h2>
        <div className="flex items-center gap-3">
          {hasActiveFilters && (
            <button
              type="button"
              onClick={handleClearAll}
              className="text-xs text-electric-blue hover:underline font-sandvik-text"
            >
              Reset all
            </button>
          )}
          {isMobile && (
            <button
              type="button"
              onClick={onClose}
              aria-label="Close filters"
              className="text-n5 hover:text-carbon-black transition-colors"
            >
              <span className="material-symbols-outlined text-xl">close</span>
            </button>
          )}
        </div>
      </div>

      {/* Active filter pills */}
      <div className="pt-3">
        <ActiveFilterPills
          facets={facets}
          activeFilters={effectiveFilters}
          onRemove={handleFilterChange}
        />
      </div>

      {/* Facet sections */}
      <div className="flex-1 overflow-y-auto pt-1">
        {facets.map((facet) => {
          if (facet.type === 'checkbox') {
            return (
              <CheckboxFacet
                key={facet.id}
                facet={facet}
                products={products}
                activeValues={effectiveFilters[facet.id]}
                onChange={handleFilterChange}
              />
            );
          }
          if (facet.type === 'range') {
            return (
              <RangeFacet
                key={facet.id}
                facet={facet}
                products={products}
                activeRange={effectiveFilters[facet.id]}
                onChange={handleFilterChange}
              />
            );
          }
          return null;
        })}
      </div>

      {/* Mobile apply button */}
      {isMobile && (
        <div className="pt-4 border-t border-n2 mt-auto">
          <button
            type="button"
            onClick={handleApply}
            className="w-full py-3 bg-electric-blue text-white text-sm font-sandvik-text
                       font-medium rounded-none hover:bg-blue-700 transition-colors"
          >
            Apply filters
          </button>
        </div>
      )}
    </div>
  );

  // Mobile: render as a slide-out overlay
  if (isMobile) {
    return (
      <>
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-carbon-black/40 z-40"
          onClick={onClose}
          aria-hidden="true"
        />
        {/* Drawer */}
        <aside
          className="fixed inset-y-0 left-0 w-[320px] max-w-[85vw] bg-white z-50
                     shadow-xl p-5 overflow-y-auto animate-slide-in-left"
          role="dialog"
          aria-label="Product filters"
        >
          {panelContent}
        </aside>
      </>
    );
  }

  // Desktop: static sidebar
  return (
    <aside className="w-[280px] flex-shrink-0 pr-6" aria-label="Product filters">
      {panelContent}
    </aside>
  );
}
